import { PlusOutlined } from '@ant-design/icons';
import { LoadingCommon } from '@components';
import { EmptyResultComponent } from '@components/EmptyResultComponent/EmptyResultComponent';
import { InputOnlyNumber } from '@components/InputOnlyNumber';
import { LabelForm } from '@components/LabelForm/LabelForm';
import {
  COMMON_ERR_NO_PERMISSION,
  PLACEHOLDER_INPUT,
  images,
} from '@constants';
import { ArrowLeftIcon } from '@constants/svgs';
import { handleShowToastError } from '@utils/helpers';
import { USER_ROLE } from '@utils/constants';
import { useCurrentUserRole } from '@hooks';
import {
  Button,
  Col,
  ConfigProvider,
  Form,
  Input,
  Row,
  Select,
  Table,
  Typography,
} from 'antd';
import { Content } from 'antd/lib/layout/layout';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';
import { adminPaths } from 'routers/routes';
import { EModalType } from 'types/others';
import {
  CompanyPageWrapper,
  ModalDeleteWrapper,
  ModalWrapper,
} from './CompanyPageStyled';
import {
  COMPANY_CONSTANT,
  HEIGHT_TABLE_SCROLL,
  MAX_LENGTH_5,
  MAX_LENGTH_50,
} from './constants';
import useCompanyPage from './hooks/useCompanyPage';

const { FORM_NAME, TITLE, LABEL, NAME, MESSAGE } = COMPANY_CONSTANT;
const SEARCH_FORM_DROPDOWN_CLASS_NAME = 'company-page-body__search-drop-down';

const CompanyPage = () => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const currentUserRole = useCurrentUserRole();

  const { state, handler } = useCompanyPage();
  const FIELDS_CONFIG = {
    COMPANY: {
      required: false,
      label: t('V_SHOT005.COMPANY'),
      name: FORM_NAME.COMPANY,
      className: SEARCH_FORM_DROPDOWN_CLASS_NAME,
    },

    COMPANY_CODE: {
      required: false,
      label:
        state.modalType != EModalType.DETAIL ? (
          <LabelForm label={LABEL.COMPANY_CODE} />
        ) : (
          LABEL.COMPANY_CODE
        ),
      name: NAME.COMPANY_CODE,
    },

    COMPANY_NAME: {
      required: false,
      label:
        state.modalType != EModalType.DETAIL ? (
          <LabelForm label={LABEL.COMPANY_NAME} />
        ) : (
          LABEL.COMPANY_NAME
        ),
      name: NAME.COMPANY_NAME,
    },
  };

  const handleClearSearch = () => {
    form?.resetFields();
    state.form?.resetFields();
  };

  const handleChangeCompanySelector = (value: any) => {
    if (value) {
      form?.setFieldValue(FORM_NAME.COMPANY, value);
    }
  };

  const handleRedirectUser = () => {
    handleShowToastError(
      COMMON_ERR_NO_PERMISSION,
      t('COMPANY_PAGE.PAGE_TITLE')
    );
    return <Navigate to={adminPaths.fixedAssetLedger} />;
  };

  return (
    <>
      {currentUserRole === USER_ROLE.STORE_OWNER ? (
        handleRedirectUser()
      ) : (
        <LoadingCommon loading={state.loadingPage}>
          <CompanyPageWrapper listdata={state.listCompany.length}>
            <Content className="company-page-header">
              <Typography className="company-page-header_title">
                {t('COMPANY_PAGE.PAGE_TITLE')}
              </Typography>
            </Content>
            <Content className="company-page-body">
              <Form
                form={state.form}
                name="company-page-search-form"
                layout="vertical"
                onFinish={handler.handleFinish}
              >
                <ConfigProvider
                  theme={{
                    components: {
                      Select: {
                        optionSelectedBg:
                          'var(--color-hover-primary) !important',
                      },
                    },
                  }}
                >
                  <Row gutter={[8, 16]} style={{ padding: '16px' }}>
                    <Col span={5}>
                      <Form.Item {...FIELDS_CONFIG.COMPANY}>
                        <Select
                          // size="large"
                          placeholder={t('COMMON.DROP_DOWN_PLACE_HOLDER')}
                          options={state.companies}
                          onChange={handleChangeCompanySelector}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={2}>
                      <Button
                        className="company-page-body__search-btn"
                        htmlType="submit"
                        autoInsertSpace={false}
                      >
                        {t('COMMON.SEARCH')}
                      </Button>
                    </Col>
                    <Col span={2}>
                      <Button
                        className="company-page-body__clear-btn"
                        onClick={handleClearSearch}
                        autoInsertSpace={false}
                      >
                        {t('COMMON.CLEAR')}
                      </Button>
                    </Col>
                    <Col span={12} />
                    <Col span={3}>
                      <Button
                        className="company-page-body__add-new-btn"
                        onClick={handler.handleOpenModalRegistration}
                        icon={<PlusOutlined />}
                        autoInsertSpace={false}
                        disabled={state.disableBtnOther}
                      >
                        {t('COMPANY_PAGE.NEW_COMPANY_REGIS')}
                      </Button>
                    </Col>
                  </Row>
                </ConfigProvider>
              </Form>
              <div className="company-page-body__alert-table">
                <ConfigProvider
                  renderEmpty={() => <EmptyResultComponent />}
                  theme={{
                    components: {
                      Table: {
                        headerBg: 'var(--background-header-table)',
                        borderColor: 'var(--color-border-secondary)',
                        headerColor: 'var(--color-header-table)',
                        rowHoverBg: 'var(--color-hover-primary)',
                      },
                    },
                  }}
                >
                  <Table
                    ref={state.tblRef}
                    rowKey={(record: any) => record.id}
                    onScroll={handler.handleScroll}
                    dataSource={state.listCompany}
                    columns={state.columns}
                    tableLayout="fixed"
                    bordered
                    virtual={state.listCompany.length > 0}
                    showSorterTooltip={false}
                    scroll={{
                      x: 1760,
                      y: HEIGHT_TABLE_SCROLL,
                    }}
                    pagination={false}
                  />
                </ConfigProvider>
              </div>
            </Content>
          </CompanyPageWrapper>

          <ModalWrapper
            width={800}
            title={
              <>
                {state.modalType === EModalType.CONFIRM_REGISTRATION && (
                  <button
                    onClick={() =>
                      handler.setModalType(EModalType.REGISTRATION)
                    }
                  >
                    <ArrowLeftIcon />
                  </button>
                )}
                {state.modalType === EModalType.CONFIRM_UPDATE && (
                  <button
                    onClick={() => handler.setModalType(EModalType.UPDATE)}
                  >
                    <ArrowLeftIcon />
                  </button>
                )}
                <span>{state.renderTextModal.title}</span>
              </>
            }
            closeIcon={false}
            maskClosable={false}
            okText={state.renderTextModal.okText}
            cancelText={TITLE.BTN_CANCEL}
            destroyOnClose={true}
            open={state.modalOpen}
            centered
            onCancel={() => {
              handler.handleCloseModal();
            }}
            loading={state.loadingModal}
            confirmLoading={state.loadingExist}
            onOk={() => {
              if (state.modalType === EModalType.DETAIL) {
                handler.handleCloseModal();
              } else if (state.modalType === EModalType.UPDATE) {
                handler.handleValidateForm();
              } else if (state.modalType === EModalType.CONFIRM_REGISTRATION) {
                handler.handleOpenModalConfirmFinal();
              } else if (state.modalType === EModalType.CONFIRM_UPDATE) {
                handler.handleOpenModalConfirmFinal();
              } else {
                handler.handleValidateForm();
              }
            }}
            className="regis-company-modal"
            okButtonProps={{
              style: {
                width: state.modalType === EModalType.DETAIL ? '100%' : '368px',
              },
              disabled: state.disableBtnNext,
              className: state.disableBtnNext
                ? 'regis-company-modal__regis-disable'
                : 'regis-company-modal__regis-ok',
            }}
            cancelButtonProps={{
              style: {
                display:
                  state.modalType === EModalType.DETAIL ? 'none' : 'block',
              },
              className: 'regis-company-modal__regis-cancel',
            }}
          >
            <Form
              layout="vertical"
              form={state.formModal}
              preserve={false}
              disabled={state.disableFields}
            >
              <div className="company-page_modal-form-left">
                <Form.Item {...FIELDS_CONFIG.COMPANY_CODE}>
                  <InputOnlyNumber
                    onlyNumber
                    placeholder={PLACEHOLDER_INPUT}
                    size="large"
                    maxLength={MAX_LENGTH_5}
                    onBlur={(event) => {
                      state.formModal.setFieldValue(
                        NAME.COMPANY_CODE,
                        event.target.value.trim()
                      );
                    }}
                  />
                </Form.Item>
              </div>

              <div className="company-page_modal-form-right">
                <Form.Item {...FIELDS_CONFIG.COMPANY_NAME}>
                  <Input
                    placeholder={PLACEHOLDER_INPUT}
                    size="large"
                    maxLength={MAX_LENGTH_50}
                    onBlur={(event) => {
                      state.formModal.setFieldValue(
                        NAME.COMPANY_NAME,
                        event.target.value.trim()
                      );
                    }}
                  />
                </Form.Item>
              </div>
            </Form>
          </ModalWrapper>

          {/* Modal delete */}
          <ModalDeleteWrapper
            centered
            closeIcon={false}
            open={state.modalDelete}
            title={TITLE.TITLE_DELETE}
            confirmLoading={state.loadingPage}
            onOk={handler.handleOkModalDelete}
            onCancel={handler.handleCloseModalDelete}
            maskClosable={false}
            cancelText={TITLE.BTN_CANCEL}
            okText={TITLE.BTN_DELETE}
            width={416}
            className="delete-company-modal"
            okButtonProps={{
              className: 'delete-company-modal__delete-ok',
            }}
            cancelButtonProps={{
              className: 'delete-company-modal__delete-cancel',
            }}
          >
            <img src={images.modalDelete} alt="modal_delete" />
            <div className="company-page_modal-delete">
              <p>{MESSAGE.CONFIRM_DELETE}</p>
              <p>
                {state.company?.companyCode}_{state.company?.companyName}
              </p>
            </div>
          </ModalDeleteWrapper>

          {/* Modal registration/update/delete success/fail */}
          <ModalDeleteWrapper
            centered
            closeIcon={false}
            open={state.modalSuccess}
            title={state.renderTextModal.title}
            onOk={handler.handleCloseModalSuccess}
            maskClosable={false}
            okText={state.renderTextModal.okText}
            width={412}
            okButtonProps={{
              style: { width: '100%', marginInlineStart: 0 },
              className: 'company-page_modal-regist-success_btn-ok',
            }}
            cancelButtonProps={{ style: { display: 'none' } }}
          >
            <img src={state.renderTextModal.image} alt="company_modal" />
            <div className="company-page_modal-regist-success">
              <p>{state.renderTextModal.message}</p>
            </div>
          </ModalDeleteWrapper>

          {/* Modal registration/update confirm */}
          <ModalDeleteWrapper
            centered
            closeIcon={false}
            open={state.modalConfirm}
            title={state.renderTextModalConfirm.title}
            onOk={handler.handleOkModalConfirmFinal}
            onCancel={handler.handleCloseModalConfirmFinal}
            maskClosable={false}
            okText={state.renderTextModalConfirm.okText}
            cancelText={TITLE.BTN_CANCEL}
            width={412}
            zIndex={9999}
            confirmLoading={state.loadingPage}
            className="modal-confirm-final"
            okButtonProps={{
              className: 'modal-confirm-final__btn-ok',
            }}
            cancelButtonProps={{
              className: 'modal-confirm-final__btn-cancel',
            }}
          >
            <img src={images.popUpConfirmFinal} alt="company_modal" />
            <div className="company-page_modal-confirm">
              <p>{state.renderTextModalConfirm.message}</p>
            </div>
          </ModalDeleteWrapper>
        </LoadingCommon>
      )}
    </>
  );
};

export { CompanyPage };
